import React from 'react';

import { ApolloProvider } from 'react-apollo';
import {ApolloLink, Observable, split} from 'apollo-link'
import {InMemoryCache, IntrospectionFragmentMatcher} from 'apollo-cache-inmemory';
import { startApplicationStatePersistence } from '../context/apollo-state-persistence';
import { Auth } from '@aws-amplify/auth';
import { onError } from 'apollo-link-error';
import config from '../config';
import ApolloClient from 'apollo-client';
import { HttpLink } from 'apollo-boost';
import { RetryLink } from 'apollo-link-retry';
import defaults from '../defaultState';
import DebounceLink from 'apollo-link-debounce';
import Resolvers from "resolvers";
import TypeDefs from "typeDefs";
import introspectionQueryResultData from '__generated__/fragmentTypes.json';
import { getMainDefinition } from 'apollo-utilities';
import { createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

const DEFAULT_DEBOUNCE_TIMEOUT = 1000;

// TODO: When upgrading to Apollo Client V3, this needs to be replaced.
const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData
})

const cache = new InMemoryCache({
    dataIdFromObject: o => (o._id ? `${o.__typename}:${o._id}`: null),
    cacheRedirects: {
        Query: {
            // If every item in the cache exists, then this is a cache hit...if the docs are right...
            resolve_entity_ids: (_, args, { getCacheKey }) => {
                return args.ids.map((id) => getCacheKey({ __typename: args.typename, _id: id }));
            }
        }
    },
    fragmentMatcher
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
          console.log('[Graphql Error]', err);
        switch (err.errorType) {
          case 'UnauthorizedException':
            // error code is set to UNAUTHENTICATED
            // when AuthenticationError thrown in resolver

            // modify the operation context with a new token
            return new Observable(async observer => {
                try{
                    // const creds = await Auth.currentUserCredentials();
                    // creds.refresh(err => {throw Error(err)});
                    const idToken = await Auth.currentSession().idToken;
                    console.log('currentSession', await Auth.currentSession());
                    const oldHeaders = operation.getContext().headers;
                    operation.setContext({
                        headers: {
                            ...oldHeaders,
                            authorization: idToken.jwtToken,
                        },
                    });
                    const subscriber = {
                        next: observer.next.bind(observer),
                        error: observer.error.bind(observer),
                        complete: observer.complete.bind(observer)
                    }
                    forward(operation).subscribe(subscriber);
                }catch(error){
                    console.error('ID Token refresh failed', err)
                    observer.error(error)
                }
            })
          default:
            break;
        }
      }
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
      // if you would also like to retry automatically on
      // network errors, we recommend that you use
      // apollo-link-retry
    }
  }
);

const appsyncAuth = {
    type: config.appSync.auth.type, // 'AMAZON_COGNITO_USER_POOLS'
    jwtToken: async () => {
        const session = await Auth.currentSession();
        return session.idToken.jwtToken;
    }
};

const appsyncConfig = {
    url: config.appSync.httpUrl,
    region: config.appSync.region,
    auth: appsyncAuth
};

const appsyncAuthLink = createAuthLink(appsyncConfig);
const appsyncSubsLink = createSubscriptionHandshakeLink(appsyncConfig, new HttpLink({ uri: config.appSync.httpUrl }));

const dataSourceLink = split(
    // split based on operation type
    ({ query }) => {
        const definition = getMainDefinition(query);
        return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
        );
    },
    appsyncSubsLink,
    new HttpLink({ uri: config.appSync.httpUrl }),
)

// Seed defaults first so restoring a partial preference snapshot preserves new defaults.
cache.writeData({ data: defaults });
startApplicationStatePersistence(cache, window.localStorage);

// App initialization still waits for restoration before loading the current session.
export const apolloCacheReady = Promise.resolve();

const client = new ApolloClient({
    cache,
    resolvers: Resolvers,
    typeDefs: TypeDefs,
    link: ApolloLink.from([
        errorLink,
        new RetryLink(),
        appsyncAuthLink,
        new DebounceLink(DEFAULT_DEBOUNCE_TIMEOUT),
        dataSourceLink
    ])
})

client.onResetStore(() => cache.writeData({ data: defaults }));
client.onClearStore(() => cache.writeData({ data: defaults }));

const withApolloProvider = (Component) => {
    return props => (
        <ApolloProvider client={client}>
                <Component {...props} />
        </ApolloProvider>
    )
}

export default withApolloProvider
